import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Tooltip,
  Space
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  DownloadOutlined,
  UploadOutlined,
  InsertRowBelowOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import phieuChamService from '../../services/phieuChamService';
import type { PhieuCham, TieuChi } from '../../../../shared/types';

const { Title } = Typography;

const PhieuChamManagementPage: React.FC = () => {
  const [data, setData] = useState<PhieuCham[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PhieuCham | null>(null);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await phieuChamService.getPhieuChamList();
      if (res.success) {
        setData(res.data || []);
      }
    } catch {
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEditWithTieuChi = async (record: PhieuCham) => {
    setEditing(record);
    const res = await phieuChamService.getTieuChiList(record.PhieuID);
    form.setFieldsValue({
      TenPhieu: record.TenPhieu,
      MoTa: record.MoTa,
      tieuChiList: res.success ? res.data : []
    });
    setModalOpen(true);
  };

  const handleDelete = async (record: PhieuCham) => {
    Modal.confirm({
      title: 'Xác nhận xóa?',
      icon: <ExclamationCircleOutlined />,
      content: `Bạn có chắc muốn xóa phiếu "${record.TenPhieu}"?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await phieuChamService.deletePhieuCham(record.PhieuID);
          message.success('Đã xóa phiếu thành công');
          fetchData();
        } catch (error) {
          message.error('Lỗi khi xóa phiếu');
        }
      },
    });
  };

  const handleClonePhieu = async (record: PhieuCham) => {
    const tieuChiList = (await phieuChamService.getTieuChiList(record.PhieuID)).data || [];
    Modal.confirm({
      title: 'Nhân bản phiếu',
      content: 'Một phiếu mới giống hệt sẽ được tạo. Tiếp tục?',
      onOk: async () => {
        try {
          await phieuChamService.createPhieuCham({
            TenPhieu: `${record.TenPhieu} (Bản sao)`,
            MoTa: record.MoTa,
            tieuChiList,
          });
          message.success('Nhân bản phiếu thành công');
          fetchData();
        } catch (error) {
          message.error('Lỗi khi nhân bản phiếu');
        }
      }
    });
  };

  const handleExportTemplate = () => {
    const ws_data = [
      ["TenPhieu", "Mota", "", ""],
      ["Phiếu chấm mẫu", "Mô tả phiếu", "", ""],
      ["Nhóm A", "", "", ""],
      ["", "Tiêu chí A1", 0.5, ""],
      ["", "Tiêu chí A2", 0.5, ""],
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "phieu_cham_template.xlsx");
  };

  const handleImportExcel = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx, .xls';
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (!target.files) return;
      const file = target.files[0];
      
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as string[][];

      if (rows.length < 2) {
        message.error('File không đúng template hoặc không có dữ liệu.');
        return;
      }
      
      const tenPhieu = rows[1][0] || '';
      const moTa = rows[1][1] || '';
      let currentGroup = '';
      const tieuChiList: any[] = [];

      for (let i = 2; i < rows.length; ++i) {
        const row = rows[i];
        if (row[0] && !row[1] && !row[2]) {
          currentGroup = row[0];
        } else if ((row[1] || row[2]) && currentGroup) {
          tieuChiList.push({ NoiDung: currentGroup, TieuChi: row[1] || '', Diem: row[2] || 0 });
        } else if (row[0] && row[1]) {
          tieuChiList.push({ NoiDung: row[0], TieuChi: row[1], Diem: row[2] || 0 });
        }
      }
      
      if (!tenPhieu || tieuChiList.length === 0) {
        message.error('File thiếu thông tin tên phiếu hoặc tiêu chí.');
        return;
      }

      Modal.confirm({
        title: 'Xác nhận import phiếu?',
        content: (
          <div>
            <p><strong>Tên phiếu:</strong> {tenPhieu}</p>
            <p><strong>Số tiêu chí:</strong> {tieuChiList.length}</p>
          </div>
        ),
        onOk: async () => {
          try {
            await phieuChamService.createPhieuCham({ TenPhieu: tenPhieu, MoTa: moTa, tieuChiList });
            message.success('Import thành công!');
            fetchData();
          } catch (error) {
            message.error('Lỗi khi import phiếu.');
          }
        }
      });
    };
    input.click();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      let currentGroup = '';
      const processedList = values.tieuChiList.map((tc: any) => {
        if (tc.NoiDung && (!tc.TieuChi && !tc.Diem)) {
          currentGroup = tc.NoiDung;
          return null;
        } else if (!tc.NoiDung && (tc.TieuChi || tc.Diem)) {
          return { ...tc, NoiDung: currentGroup };
        }
        return tc;
      }).filter(Boolean);

      const payload = { ...values, tieuChiList: processedList };
      if (editing) {
        await phieuChamService.updatePhieuCham(editing.PhieuID, payload);
      } else {
        await phieuChamService.createPhieuCham(payload);
      }
      message.success(editing ? 'Cập nhật thành công' : 'Tạo mới thành công');
      setModalOpen(false);
      fetchData();
    } catch (error) {
      message.error('Lỗi khi lưu phiếu');
    }
  };

  const columns = [
    { title: 'Mã phiếu', dataIndex: 'PhieuID', key: 'PhieuID' },
    { title: 'Tên phiếu', dataIndex: 'TenPhieu', key: 'TenPhieu' },
    { title: 'Mô tả', dataIndex: 'MoTa', key: 'MoTa' },
    { 
      title: 'Ngày tạo', 
      dataIndex: 'CreatedAt', 
      key: 'CreatedAt',
      render: (text: string) => new Date(text).toLocaleString('vi-VN')
    },
    { title: 'Người tạo', dataIndex: 'CreatedBy', key: 'CreatedBy' },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: PhieuCham) => (
        <Space>
          <Tooltip title="Sửa phiếu">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEditWithTieuChi(record)}>Sửa</Button>
          </Tooltip>
          <Tooltip title="Xóa phiếu">
            <Button type="link" icon={<DeleteOutlined />} danger onClick={() => handleDelete(record)}>Xóa</Button>
          </Tooltip>
          <Tooltip title="Nhân bản phiếu">
            <Button type="link" icon={<CopyOutlined />} onClick={() => handleClonePhieu(record)}>Nhân bản</Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2} style={{ textAlign: 'left' }}>Quản lý Phiếu chấm</Title>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Thêm phiếu mới</Button>
          <Button icon={<UploadOutlined />} onClick={handleImportExcel}>Import từ Excel</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExportTemplate}>Tải file mẫu</Button>
        </Space>
        <Table rowKey="PhieuID" loading={loading} dataSource={data} columns={columns} bordered />
        <Modal
          open={modalOpen}
          title={editing ? 'Cập nhật phiếu' : 'Thêm phiếu chấm điểm'}
          okText="Lưu"
          onCancel={() => setModalOpen(false)}
          onOk={handleModalOk}
          confirmLoading={loading}
          width={900}
        >
          <Form form={form} layout="vertical">
            <Form.Item name="TenPhieu" label="Tên phiếu" rules={[{ required: true, message: 'Nhập tên phiếu' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="MoTa" label="Mô tả">
              <Input.TextArea />
            </Form.Item>
            <Form.List name="tieuChiList">
              {(fields, { add, remove, move }) => (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Danh sách tiêu chí</div>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item {...restField} name={[name, 'NoiDung']} style={{ flex: 2 }}><Input placeholder="Nội dung/nhóm" /></Form.Item>
                      <Form.Item {...restField} name={[name, 'TieuChi']} style={{ flex: 3 }}><Input placeholder="Tiêu chí" /></Form.Item>
                      <Form.Item {...restField} name={[name, 'Diem']} style={{ width: 80 }}><Input type="number" placeholder="Điểm" /></Form.Item>
                      <Tooltip title="Xóa"><Button icon={<DeleteOutlined />} onClick={() => remove(name)} danger type="text" /></Tooltip>
                      <Tooltip title="Chèn tiêu chí phía dưới"><Button icon={<InsertRowBelowOutlined />} onClick={() => add({}, index + 1)} type="text" /></Tooltip>
                      <Tooltip title="Nhân bản"><Button icon={<CopyOutlined />} onClick={() => add(form.getFieldValue(['tieuChiList', index]))} type="text" /></Tooltip>
                    </Space>
                  ))}
                  <Space>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Thêm tiêu chí</Button>
                    <Button type="dashed" onClick={() => add({ NoiDung: 'Tên nhóm mới', TieuChi: '', Diem: null })} block icon={<PlusOutlined />}>Thêm dòng nhóm</Button>
                  </Space>
                </>
              )}
            </Form.List>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default PhieuChamManagementPage; 